
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UseBillActionsProps {
  onSuccess: () => Promise<void>;
  setUpdating: (updating: boolean) => void;
}

export const useBillActions = ({ onSuccess, setUpdating }: UseBillActionsProps) => {
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<'paid' | 'cancel'>('paid');
  const { toast } = useToast();

  const handleUpdateStatus = async () => {
    if (!selectedBillId) return;

    try {
      setUpdating(true);
      
      // 1. Update the bill status
      const { error } = await supabase
        .from('bills')
        .update({ status: dialogAction === 'paid' ? 'paid' : 'cancelled' })
        .eq('id', selectedBillId);

      if (error) throw error;

      // 2. Get the subscription ID for the bill
      const { data: bill, error: billError } = await supabase
        .from('bills')
        .select('subscription_id')
        .eq('id', selectedBillId)
        .single();
        
      if (billError) {
        console.error("Error getting bill subscription:", billError);
      }
      
      // 3. If we have a subscription ID and action is 'paid', update subscription if needed
      if (bill?.subscription_id && dialogAction === 'paid') {
        console.log(`Checking all bills for subscription ${bill.subscription_id}`);
        
        // Check if all bills for this subscription are paid
        const { data: unpaidBills, error: unpaidBillsError } = await supabase
          .from('bills')
          .select('id, status')
          .eq('subscription_id', bill.subscription_id)
          .eq('status', 'unpaid');
          
        if (unpaidBillsError) {
          console.error("Error checking unpaid bills:", unpaidBillsError);
        } else {
          console.log(`Found ${unpaidBills?.length || 0} unpaid bills`);
          
          // If no unpaid bills remain, update subscription to active
          if (!unpaidBills || unpaidBills.length === 0) {
            console.log(`All bills paid, updating subscription ${bill.subscription_id} to active`);
            
            const { data: updateResult, error: subscriptionError } = await supabase
              .from('subscriptions')
              .update({ status: 'active' })
              .eq('id', bill.subscription_id)
              .select();
              
            if (subscriptionError) {
              console.error("Error updating subscription status:", subscriptionError);
            } else {
              console.log("Subscription update result:", updateResult);
            }
          } else {
            console.log(`${unpaidBills.length} unpaid bills remain, keeping subscription in pending state`);
          }
        }
      } else if (bill?.subscription_id && dialogAction === 'cancel') {
        // If cancelling, update subscription status to cancelled
        const { error: subscriptionError } = await supabase
          .from('subscriptions')
          .update({ status: 'cancelled' })
          .eq('id', bill.subscription_id);
          
        if (subscriptionError) {
          console.error("Error updating subscription status:", subscriptionError);
        }
      }

      toast({
        title: "Success",
        description: `Bill ${dialogAction === 'paid' ? 'marked as paid' : 'cancelled'} successfully`,
        duration: 5000,
      });

      setIsDialogOpen(false);
      document.body.style.removeProperty('pointer-events');

      await onSuccess();
    } catch (error) {
      console.error('Error updating bill status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to ${dialogAction === 'paid' ? 'mark bill as paid' : 'cancel bill'}`,
        duration: 5000,
      });
    } finally {
      setUpdating(false);
      setSelectedBillId(null);
      document.body.style.removeProperty('pointer-events');
    }
  };

  const handleUpdateClick = (billId: string) => {
    setDialogAction('paid');
    setSelectedBillId(billId);
    setIsDialogOpen(true);
  };

  const handleCancelClick = (billId: string) => {
    setDialogAction('cancel');
    setSelectedBillId(billId);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      document.body.style.removeProperty('pointer-events');
      setIsDialogOpen(false);
      setSelectedBillId(null);
    } else {
      setIsDialogOpen(true);
    }
  };

  return {
    selectedBillId,
    isDialogOpen,
    dialogAction,
    handleUpdateStatus,
    handleUpdateClick,
    handleCancelClick,
    handleDialogClose
  };
};
